import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { ROOMS } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toISODate = (date) => date.toISOString().slice(0, 10);

function plusDaysISO(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return toISODate(date);
}

export default function BookingForm({ initialRoom, onSuccess }) {
  const { t, lang } = useI18n();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    room_id: initialRoom,
    check_in: "",
    check_out: "",
    guests: 2,
    message: "",
  });

  useEffect(() => {
    if (initialRoom) setForm((prev) => ({ ...prev, room_id: initialRoom }));
  }, [initialRoom]);

  const handle = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        guests: Number(form.guests),
        language: lang,
      });
      toast.success(t("booking.success_title"));
      onSuccess();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        t("booking.error");
      toast.error(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="md:col-span-7 bg-white p-8 md:p-12 border border-[#6B4A2B]/10"
      data-testid="booking-form"
    >
      <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
        <Field label={t("booking.first_name")}>
          <input
            required
            data-testid="booking-first-name"
            className="paper-input"
            value={form.first_name}
            onChange={handle("first_name")}
          />
        </Field>
        <Field label={t("booking.last_name")}>
          <input
            required
            data-testid="booking-last-name"
            className="paper-input"
            value={form.last_name}
            onChange={handle("last_name")}
          />
        </Field>
        <Field label={t("booking.email")}>
          <input
            required
            type="email"
            data-testid="booking-email"
            className="paper-input"
            value={form.email}
            onChange={handle("email")}
          />
        </Field>
        <Field label={t("booking.phone")}>
          <input
            required
            type="tel"
            data-testid="booking-phone"
            className="paper-input"
            placeholder="+229 ..."
            value={form.phone}
            onChange={handle("phone")}
          />
        </Field>

        <Field label={t("booking.room")} className="md:col-span-2">
          <select
            required
            data-testid="booking-room"
            className="paper-input"
            value={form.room_id}
            onChange={handle("room_id")}
          >
            <option value="" disabled>
              {t("booking.select_room")}
            </option>
            {ROOMS.map((r) => {
              const roomLabel = lang === "fr" ? r.nameFr : r.nameEn;
              const priceLabel = r.priceTo
                ? `${r.priceFrom}-${r.priceTo}€`
                : `${r.priceFrom}€`;
              return (
                <option key={r.id} value={r.id}>
                  {`${roomLabel} · ${priceLabel}`}
                </option>
              );
            })}
          </select>
        </Field>

        <Field label={t("booking.check_in")}>
          <input
            required
            type="date"
            data-testid="booking-check-in"
            className="paper-input"
            min={toISODate(new Date())}
            value={form.check_in}
            onChange={handle("check_in")}
          />
        </Field>
        <Field label={t("booking.check_out")}>
          <input
            required
            type="date"
            data-testid="booking-check-out"
            className="paper-input"
            min={form.check_in || plusDaysISO(2)}
            value={form.check_out}
            onChange={handle("check_out")}
          />
        </Field>

        <Field label={t("booking.guests")}>
          <input
            required
            type="number"
            min={1}
            max={8}
            data-testid="booking-guests"
            className="paper-input"
            value={form.guests}
            onChange={handle("guests")}
          />
        </Field>

        <Field label={t("booking.message")} className="md:col-span-2">
          <textarea
            data-testid="booking-message"
            className="paper-input resize-none"
            rows={4}
            placeholder={t("booking.message_ph")}
            value={form.message}
            onChange={handle("message")}
          />
        </Field>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="booking-submit"
        className="btn-primary mt-10"
      >
        {submitting ? t("booking.submitting") : t("booking.submit")}
        {!submitting && <ArrowUpRight size={14} />}
      </button>
    </form>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="overline block mb-2 text-[#5C5042]">{label}</span>
      {children}
    </label>
  );
}
